/**
 * useBillingUsage — signup-billing-mvp Phase 5 Session 7 (2026-05-13).
 *
 * Reads the caller's `usage_balance` row via `GET /api/billing/usage`. Two
 * possible response shapes:
 *
 *   1. **Admin envelope** — `{ admin: true, unmetered: true }`. Phase 5
 *      Session 4 added this branch on the API controller (admins are
 *      unmetered + never debited). The check is JWT-backed
 *      (`req.auth.user.app_metadata.role === 'admin'`); the original
 *      `req.userProfile.role` read was a 2026-05-13 field-collision bug
 *      fixed across `requireAdmin`/`getAdminStatus` on 2026-05-14 and
 *      across `getUsage` on 2026-05-25.
 *   2. **Regular row** — full `usage_balance` row OR `null` when the user
 *      hasn't subscribed yet.
 *
 * Consumers:
 *   - `__root.tsx` redirect cascade — `!balance` → redirect to
 *     `/billing/checkout`; `balance.locked` → same.
 *   - `<CheckoutSuccessPolling>` — `refetchInterval: 1000` polls for up to
 *     10 attempts (10s total) to detect the `customer.subscription.created`
 *     webhook landing.
 *
 * The hook does NOT discriminate between the admin envelope and the row
 * shape — the precedence ladder in `__root.helpers.ts` runs the admin
 * check BEFORE the balance check, so admins bypass the `!balance` branch
 * regardless of which shape this hook returns.
 */

import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';
import { useAuth } from './use-auth';
import { useUserProfile } from './use-user-profile';

/**
 * Shape of the enriched `usage_balance` projection returned by
 * `GET /api/billing/usage`. Mirrors `UsageBalanceRow` from
 * `@arcanum-hq/supabase` — but locally declared here (NOT type-imported)
 * because the supabase package's `Tables<'usage_balance'>` type carries the
 * auto-generated `Database` shape which forces the web's vitest to load too
 * much.
 *
 * Phase 6 Session 1 (2026-05-13) added `pct` + `tier_display_name`. The wire
 * shape mirrors `EnrichedUsageBalanceRow` in `apps/api/src/modules/billing/
 * billing.service.ts` — both adds are server-computed (single-fetch
 * resolution for Settings → Billing avoids a separate tiers query).
 */
export interface BillingUsageRow {
  user_id: string;
  tier_id: string;
  stripe_customer_id: string | null;
  stripe_subscription_id: string | null;
  monthly_grant_cogs: number;
  monthly_grant_used_cogs: number;
  period_start: string;
  period_end: string;
  locked: boolean;
  usage_limiting_enabled: boolean;
  grant_overridden: boolean;
  created_at: string;
  updated_at: string;
  pct: number; // server-computed `used / grant`; 0 when grant=0
  tier_display_name: string; // joined from subscription_tiers
  cancel_at: string | null; // ISO timestamp when subscription is scheduled to cancel via Stripe Portal; null otherwise
}

/** Admin-bypass envelope returned for admin callers. */
export interface BillingUsageAdminEnvelope {
  admin: true;
  unmetered: true;
}

export type BillingUsageResponse =
  | BillingUsageRow
  | BillingUsageAdminEnvelope
  | null;

interface UsageEnvelope {
  data: BillingUsageResponse;
}

interface UseBillingUsageOptions {
  /**
   * When set, refetch every N ms. Used by `<CheckoutSuccessPolling>` to
   * poll for subscription.created webhook landing (1s × 10 attempts).
   * Omit (or pass `false`) for normal cascade use.
   */
  refetchInterval?: number | false;
}

export function useBillingUsage(opts?: UseBillingUsageOptions) {
  const { user } = useAuth();
  // Gate the query on approval: `GET /api/billing/usage` is `requireApproved`
  // (returns 403 for pending/rejected/onboarding users), and billing only
  // matters for approved users anyway. Firing it for an unapproved user just
  // produced a benign-but-noisy 403 on every authenticated route. We gate on
  // `useUserProfile().profile.status` (NOT `useApprovalStatus().isApproved`)
  // because the `__root.tsx` redirect cascade already waits on this same
  // profile query (`profileLoading`) before reading `balance` — so billing
  // enables exactly when the cascade's approval source resolves, with no race
  // (an `isApproved`-gated query the cascade doesn't await could let an
  // approved user flash to `/billing/checkout` with `balance` still null).
  const { profile } = useUserProfile();
  const isApproved = profile?.status === 'approved';

  const query = useQuery<BillingUsageResponse>({
    queryKey: queryKeys.billing.usage(user?.id),
    queryFn: async () => {
      const envelope = await api.get<UsageEnvelope>('/api/billing/usage');
      return envelope.data;
    },
    enabled: !!user && isApproved,
    staleTime: 30_000,
    refetchInterval: opts?.refetchInterval ?? false,
  });

  return {
    data: query.data ?? null,
    isLoading: query.isLoading,
    isError: query.isError,
    refetch: query.refetch,
  };
}

/** Type guard — returns true when the API returned the admin envelope. */
export function isAdminEnvelope(
  value: BillingUsageResponse
): value is BillingUsageAdminEnvelope {
  return (
    !!value &&
    typeof value === 'object' &&
    'admin' in value &&
    value.admin === true
  );
}

/**
 * Discriminator helper for the redirect cascade. Returns the row shape OR
 * null — `__root.helpers.ts` reads non-null + non-admin-envelope only.
 */
export function asBillingUsageRow(
  value: BillingUsageResponse
): BillingUsageRow | null {
  if (!value) return null;
  if (isAdminEnvelope(value)) return null;
  return value;
}
